import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Wifi } from 'lucide-react';
import { useConnection } from '@/lib/connection';

/**
 * ReconnectToast — Phase 6 Offline-First sub-commit 3 (13 Jun 2026).
 *
 * Brief affirmative pill that confirms the network came back, after a
 * non-trivial offline period.
 *
 * Gating discipline:
 *   - Fires ONLY on a genuine `shouldSend` false→true transition. The
 *     module tracks the timestamp at which `shouldSend` first went
 *     false; the toast only shows on the next true edge.
 *   - Requires the offline period to have lasted at least
 *     RECONNECT_MIN_OFFLINE_MS (3 seconds). Spurious connectivity
 *     flips from tab focus, browser network-state churn, or quickly
 *     recovered API timeouts would otherwise flash this toast on
 *     every minor blip — exactly the noise the scope discipline
 *     named ("brief toast" must be earned, not chronic).
 *   - Auto-dismisses after RECONNECT_VISIBLE_MS (2 seconds). Short
 *     enough to read at a glance, long enough not to feel skipped.
 *
 * Visual contrast with OfflineBanner:
 *   - OfflineBanner: amber (warning hue), TOP of viewport,
 *     persistent until `shouldSend` recovers. Communicates "your
 *     state has degraded but your saved data is still here."
 *   - ReconnectToast: moor (brand neutral, NOT alarm hue), BOTTOM
 *     of viewport, transient. Communicates "your state is restored,
 *     no action needed."
 *
 * Why a separate component and a different viewport position rather
 * than morphing OfflineBanner in place: the two states are visually
 * distinct (alarm vs all-good) AND temporally distinct (persistent
 * vs transient). Reusing OfflineBanner's slot would either flash
 * the alarm-coloured banner green-then-fade (confusing colour
 * grammar) or leave a moor pill living where users have been trained
 * to expect an amber alarm (also confusing). Different position,
 * different role, clear visual contract.
 *
 * The toast does NOT trigger any network work. Actual data refresh
 * happens automatically via TanStack Query's natural staleTime +
 * the MePage outbox-drain effect on the same `shouldSend` flip.
 * This toast is purely a UX confirmation of the state transition.
 *
 * Accessibility:
 *   - role="status" + aria-live="polite" — screen readers announce
 *     the recovery but not assertively (informational, not alert).
 *   - The Wifi icon is aria-hidden because the message text already
 *     carries the meaning. Avoids screen-reader doubling.
 *
 * The "wentOfflineAt" timestamp is held in a ref rather than state
 * because tracking it does not require a re-render — it's a
 * transition observer that only matters at the next effect run.
 * Using state would schedule an extra render on every `shouldSend`
 * flip for no behavioural gain.
 */

const RECONNECT_MIN_OFFLINE_MS = 3000;
const RECONNECT_VISIBLE_MS = 2000;

export function ReconnectToast(): JSX.Element | null {
  const { t } = useTranslation('common');
  const { shouldSend } = useConnection();
  const [visible, setVisible] = useState(false);
  const wentOfflineAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!shouldSend) {
      // Going offline (or initial render while already offline).
      // Stamp once; don't overwrite on subsequent re-renders that
      // happen while still offline (e.g. lastApiResult flips within
      // the offline state). Overwriting would shorten the perceived
      // offline duration on the next recovery.
      if (wentOfflineAtRef.current === null) {
        wentOfflineAtRef.current = Date.now();
      }
      return;
    }
    // shouldSend === true. Was there a prior meaningful offline
    // period? Clear the stamp regardless — we've handled this
    // transition, and the next offline event will re-stamp.
    const wentOfflineAt = wentOfflineAtRef.current;
    wentOfflineAtRef.current = null;
    if (wentOfflineAt === null) {
      // No prior offline event observed (initial render online,
      // or already-recovered before this component mounted).
      return;
    }
    const offlineDurationMs = Date.now() - wentOfflineAt;
    if (offlineDurationMs < RECONNECT_MIN_OFFLINE_MS) {
      // Too brief — skip the toast to avoid flash-of-toast noise.
      return;
    }
    setVisible(true);
    const timer = setTimeout(
      () => setVisible(false),
      RECONNECT_VISIBLE_MS,
    );
    return () => clearTimeout(timer);
  }, [shouldSend]);

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="safe-bottom fixed inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-center gap-2 rounded-2xl bg-moor-700 px-4 py-3 text-sm font-medium text-white shadow-lg"
    >
      <Wifi aria-hidden="true" className="h-4 w-4 shrink-0" />
      <span>{t('offline.reconnected')}</span>
    </div>
  );
}
